import { useState, useEffect } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { motion } from "framer-motion";
import { ArrowLeft, ArrowUpRight, Loader2, MapPin, Check, ShieldCheck, Target, HandHeart, PawPrint } from "lucide-react";
import Seo from "@/components/Seo";
import PtfButton from "@/components/PtfButton";
import ProgrammeGallery from "@/components/ProgrammeGallery";
import ProgrammeDonate from "@/components/ProgrammeDonate";
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from "@/components/ui/accordion";
import { Sheet, SheetContent, SheetTitle } from "@/components/ui/sheet";
import { api, resolveImg } from "@/lib/api";
import { trackMetaEvent } from "@/lib/meta";
import { locationLabel, SDG_TITLE, PROGRAMME_STATUS_LABEL } from "@/lib/content";
import { SCHOOLS } from "@/lib/learning";

const STATUS_STYLES = {
  CONCEPT: "border border-ptf-bg/40 text-ptf-bg",
  "IN DEVELOPMENT": "border border-ptf-accent/60 text-ptf-accent",
  PILOT: "bg-ptf-accent/55 text-ptf-primary",
  ACTIVE: "bg-ptf-accent text-ptf-primary",
  COMPLETED: "bg-ptf-bg text-ptf-primary",
  PAUSED: "border border-ptf-bg/25 text-ptf-bg/60",
  ARCHIVED: "border border-ptf-bg/15 text-ptf-bg/40",
};

function Block({ label, children }) {
  if (!children) return null;
  return (
    <div className="py-8 border-b border-ptf-primary/12">
      <p className="text-[0.65rem] uppercase tracking-[0.24em] text-ptf-primary/50 mb-3">{label}</p>
      <p className="text-base md:text-lg text-ptf-text/80 leading-relaxed whitespace-pre-line max-w-[720px]">{children}</p>
    </div>
  );
}

function KpiGrid({ label, items, tone }) {
  if (!items?.length) return null;
  return (
    <div className="py-8 border-b border-ptf-primary/12">
      <p className="text-[0.65rem] uppercase tracking-[0.24em] text-ptf-primary/50 mb-4 flex items-center gap-2">
        {tone === "verified" ? <ShieldCheck className="h-3.5 w-3.5 text-ptf-secondary" /> : <Target className="h-3.5 w-3.5" />} {label}
      </p>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        {items.map((k, i) => (
          <div key={`${k.label}-${i}`} className="border border-ptf-primary/12 p-4">
            <p className="text-[0.62rem] uppercase tracking-[0.14em] text-ptf-text/50">{k.label}</p>
            <p className="mt-1.5 text-lg font-display text-ptf-primary">{k.value}{k.unit ? ` ${k.unit}` : ""}</p>
          </div>
        ))}
      </div>
    </div>
  );
}

function DetailAccordionItem({ value, title, children }) {
  if (!children) return null;
  return (
    <AccordionItem value={value} className="border-ptf-primary/12">
      <AccordionTrigger className="text-[0.7rem] uppercase tracking-[0.2em] text-ptf-primary py-5 hover:no-underline">{title}</AccordionTrigger>
      <AccordionContent className="text-base text-ptf-text/80 leading-relaxed max-w-[720px] whitespace-pre-line">{children}</AccordionContent>
    </AccordionItem>
  );
}

function DetailHero({ p }) {
  const cover = p.images?.[0];
  return (
    <section className="relative bg-ptf-primary text-ptf-bg overflow-hidden grain">
      {cover && (
        <>
          <img src={resolveImg(cover)} alt={p.name} className="absolute inset-0 h-full w-full object-cover opacity-30" />
          <div className="absolute inset-0 bg-gradient-to-t from-ptf-primary via-ptf-primary/70 to-ptf-primary/40" />
        </>
      )}
      <div className="relative z-10 mx-auto max-w-[1200px] px-5 sm:px-8 lg:px-12 pt-36 pb-14 md:pt-44 md:pb-20">
        <Link to="/programmes" className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.16em] text-ptf-bg/70 hover:text-ptf-accent transition-colors mb-8">
          <ArrowLeft className="h-4 w-4" /> All Programmes
        </Link>
        <div className="flex flex-wrap items-center gap-2 mb-6">
          <span data-testid="programme-status" className={`text-[0.6rem] uppercase tracking-[0.16em] px-3 py-1.5 ${STATUS_STYLES[p.status] || STATUS_STYLES.CONCEPT}`}>{PROGRAMME_STATUS_LABEL[p.status] || p.status}</span>
          <span className="text-[0.6rem] uppercase tracking-[0.16em] border border-ptf-bg/25 px-3 py-1.5 text-ptf-bg/70">{p.category}</span>
          {(p.sdgs || []).map((n) => (
            <span key={n} className="text-[0.6rem] uppercase tracking-[0.12em] border border-ptf-accent/40 px-3 py-1.5 text-ptf-accent" title={SDG_TITLE[n]}>SDG {n}</span>
          ))}
        </div>
        <motion.h1
          data-testid="programme-title"
          initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
          className="font-display font-extrabold uppercase text-4xl sm:text-5xl lg:text-6xl leading-[0.95] tracking-tight max-w-4xl"
        >
          {p.name}
        </motion.h1>
        {p.short_description && <p className="mt-6 max-w-2xl text-base md:text-lg text-ptf-bg/75 leading-relaxed">{p.short_description}</p>}
        <div className="mt-6 flex flex-wrap items-center gap-x-6 gap-y-2 text-sm text-ptf-bg/80">
          {(p.region || p.location) && (
            <span className="inline-flex items-center gap-2"><MapPin className="h-4 w-4 text-ptf-accent" />{locationLabel(p)}</span>
          )}
          {p.duration && <span>Duration: <span className="text-ptf-bg">{p.duration}</span></span>}
        </div>
      </div>
    </section>
  );
}

function AtAGlance({ p }) {
  return (
    <div data-testid="programme-glance" className="sticky top-28 border border-ptf-primary/12 p-7 bg-white">
      <p className="text-[0.65rem] uppercase tracking-[0.24em] text-ptf-primary/50 mb-4">At a glance</p>
      <dl className="space-y-4 text-sm">
        <div><dt className="text-ptf-text/50 uppercase text-[0.6rem] tracking-[0.14em]">Status</dt><dd className="text-ptf-primary font-medium mt-1">{PROGRAMME_STATUS_LABEL[p.status] || p.status}</dd></div>
        <div><dt className="text-ptf-text/50 uppercase text-[0.6rem] tracking-[0.14em]">Programme Area</dt><dd className="text-ptf-primary font-medium mt-1">{p.category}</dd></div>
        {(p.location || p.region) && <div><dt className="text-ptf-text/50 uppercase text-[0.6rem] tracking-[0.14em]">Location</dt><dd className="text-ptf-primary font-medium mt-1">{locationLabel(p)}</dd></div>}
        {p.duration && <div><dt className="text-ptf-text/50 uppercase text-[0.6rem] tracking-[0.14em]">Duration</dt><dd className="text-ptf-primary font-medium mt-1">{p.duration}</dd></div>}
        {p.target_beneficiaries && <div><dt className="text-ptf-text/50 uppercase text-[0.6rem] tracking-[0.14em]">Target Beneficiaries</dt><dd className="text-ptf-primary mt-1">{p.target_beneficiaries}</dd></div>}
        {p.budget && <div><dt className="text-ptf-text/50 uppercase text-[0.6rem] tracking-[0.14em]">Programme Budget</dt><dd className="text-ptf-primary font-medium mt-1">{p.currency} {p.budget}</dd></div>}
        {p.partners?.length > 0 && (
          <div><dt className="text-ptf-text/50 uppercase text-[0.6rem] tracking-[0.14em]">Confirmed Partners</dt><dd className="text-ptf-primary mt-1 space-y-1">{p.partners.map((pt) => <div key={pt}>{pt}</div>)}</dd></div>
        )}
        {p.proposed_partners?.length > 0 && (
          <div><dt className="text-ptf-text/50 uppercase text-[0.6rem] tracking-[0.14em]">Proposed Strategic Partners <span className="text-ptf-secondary">(not confirmed)</span></dt><dd className="text-ptf-primary/80 mt-1 space-y-1">{p.proposed_partners.map((pt) => <div key={pt}>{pt}</div>)}</dd></div>
        )}
      </dl>
      <div className="mt-8 space-y-3">
        {p.donations_enabled && <ProgrammeDonate programme={p} />}
        <PtfButton to="/partnerships" variant={p.donations_enabled ? "outline" : "primary"} className="w-full justify-center">Partner on This</PtfButton>
      </div>
    </div>
  );
}

function TrainingLink({ p }) {
  const { data: training = [] } = useQuery({
    queryKey: ["training"],
    queryFn: async () => (await api.get("/training")).data,
    enabled: (p.related_training_categories || []).length > 0,
  });
  const joinNames = (arr) => arr.length <= 1 ? (arr[0] || "") : `${arr.slice(0, -1).join(", ")} and ${arr[arr.length - 1]}`;
  const cats = p.related_training_categories || [];
  if (cats.length === 0) return null;
  const matches = training.filter((t) => cats.includes(t.category));
  const schoolNames = cats.map((slug) => SCHOOLS.find((s) => s.slug === slug)?.name).filter(Boolean);

  return (
    <div className="py-8 border-b border-ptf-primary/12" data-testid="programme-training">
      <p className="text-[0.65rem] uppercase tracking-[0.24em] text-ptf-primary/50 mb-3">Training</p>
      {p.training_component && <p className="text-base text-ptf-text/80 leading-relaxed max-w-[720px] mb-4">{p.training_component}</p>}
      {schoolNames.length > 0 && (
        <p className="text-sm text-ptf-text/60 mb-3">Connects with the {joinNames(schoolNames)} school{schoolNames.length > 1 ? "s" : ""} in Learn &amp; Train.</p>
      )}
      {matches.length > 0 ? (
        <div className="flex flex-wrap gap-2">
          {matches.slice(0, 6).map((t) => (
            <Link key={t.slug} to={`/learning/training/${t.slug}`} className="text-[0.68rem] uppercase tracking-[0.12em] px-3 py-2 border border-ptf-primary/20 text-ptf-primary hover:border-ptf-primary transition-colors inline-flex items-center gap-1.5">
              {t.title} <ArrowUpRight className="h-3 w-3" />
            </Link>
          ))}
          {matches.length > 6 && (
            <Link to="/learning/training" className="text-[0.68rem] uppercase tracking-[0.12em] px-3 py-2 text-ptf-secondary">+{matches.length - 6} more</Link>
          )}
        </div>
      ) : (
        <p className="text-sm text-ptf-text/50">Related training programmes are in development.</p>
      )}
    </div>
  );
}

function VolunteeringBlock({ p }) {
  const cats = p.related_volunteer_categories || [];
  if (cats.length === 0 && !p.volunteer_component) return null;
  return (
    <div className="py-8 border-b border-ptf-primary/12" data-testid="programme-volunteering">
      <p className="text-[0.65rem] uppercase tracking-[0.24em] text-ptf-primary/50 mb-3 flex items-center gap-2"><HandHeart className="h-3.5 w-3.5" /> Volunteering</p>
      {p.volunteer_component && <p className="text-base text-ptf-text/80 leading-relaxed max-w-[720px] mb-4">{p.volunteer_component}</p>}
      {cats.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {cats.map((c) => (
            <Link key={c} to={`/volunteer?area=${encodeURIComponent(c)}&programme=${encodeURIComponent(p.name)}`} className="text-[0.68rem] uppercase tracking-[0.12em] px-3 py-2 border border-ptf-primary/20 text-ptf-primary hover:border-ptf-primary transition-colors inline-flex items-center gap-1.5">
              {c} Volunteer <ArrowUpRight className="h-3 w-3" />
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}

function PartnershipBlock({ p }) {
  if (!(p.partnership_requirements?.length > 0)) return null;
  return (
    <div className="py-8 border-b border-ptf-primary/12" data-testid="programme-partnership">
      <p className="text-[0.65rem] uppercase tracking-[0.24em] text-ptf-primary/50 mb-3">Partnership Opportunities</p>
      <div className="flex flex-wrap gap-2 mb-4">
        {p.partnership_requirements.map((r) => (
          <span key={r} className="text-[0.65rem] uppercase tracking-[0.12em] px-3 py-1.5 bg-ptf-primary/6 text-ptf-primary">{r}</span>
        ))}
      </div>
      {p.funding_requirement && <p className="text-sm text-ptf-text/70 leading-relaxed max-w-[720px] mb-4">{p.funding_requirement}</p>}
      <PtfButton to="/partnerships" variant="outline">Discuss This Programme</PtfButton>
    </div>
  );
}

function SpeciesDetailSheet({ species, onClose }) {
  const s = species;
  return (
    <Sheet open={Boolean(s)} onOpenChange={(v) => !v && onClose()}>
      <SheetContent side="right" className="bg-ptf-bg overflow-y-auto w-full sm:max-w-lg" data-testid="species-detail-sheet">
        {s && (
          <div className="pt-4">
            {s.images?.[0] && <img src={resolveImg(s.images[0])} alt={s.common_name} className="w-full aspect-[4/3] object-cover mb-5" />}
            <SheetTitle className="font-display uppercase text-2xl tracking-tight text-ptf-primary">{s.common_name}</SheetTitle>
            {s.scientific_name && <p className="italic text-ptf-secondary mt-1">{s.scientific_name}</p>}
            <div className="mt-5 space-y-4 text-sm">
              {s.conservation_status && <div><p className="text-[0.6rem] uppercase tracking-[0.14em] text-ptf-text/50">Conservation Status</p><p className="mt-1 text-ptf-text/85">{s.conservation_status}</p></div>}
              {s.legal_protection_status && <div><p className="text-[0.6rem] uppercase tracking-[0.14em] text-ptf-text/50">Legal / Protection Status</p><p className="mt-1 text-ptf-text/85">{s.legal_protection_status}</p></div>}
              {s.habitat && <div><p className="text-[0.6rem] uppercase tracking-[0.14em] text-ptf-text/50">Habitat</p><p className="mt-1 text-ptf-text/85">{s.habitat}</p></div>}
              {s.threats && <div><p className="text-[0.6rem] uppercase tracking-[0.14em] text-ptf-text/50">Threats</p><p className="mt-1 text-ptf-text/85">{s.threats}</p></div>}
              {s.location && <div><p className="text-[0.6rem] uppercase tracking-[0.14em] text-ptf-text/50">Location</p><p className="mt-1 text-ptf-text/85">{s.location}</p></div>}
              {s.programme_role && <div><p className="text-[0.6rem] uppercase tracking-[0.14em] text-ptf-text/50">Programme Role</p><p className="mt-1 text-ptf-text/85">{s.programme_role}</p></div>}
              {s.monitoring_method && <div><p className="text-[0.6rem] uppercase tracking-[0.14em] text-ptf-text/50">Monitoring Method</p><p className="mt-1 text-ptf-text/85">{s.monitoring_method}</p></div>}
              {s.rescue_relevance && <div><p className="text-[0.6rem] uppercase tracking-[0.14em] text-ptf-text/50">Rescue Relevance</p><p className="mt-1 text-ptf-text/85">{s.rescue_relevance}</p></div>}
              {s.field_records?.length > 0 && (
                <div>
                  <p className="text-[0.6rem] uppercase tracking-[0.14em] text-ptf-text/50">Field Records</p>
                  <ul className="mt-1 space-y-1 text-ptf-text/85">{s.field_records.map((r, i) => <li key={`${r}-${i}`}>{r}</li>)}</ul>
                </div>
              )}
            </div>
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
}

function SpeciesSection({ programmeId }) {
  const [selected, setSelected] = useState(null);
  const { data: species = [] } = useQuery({
    queryKey: ["species", programmeId],
    queryFn: async () => (await api.get(`/species?programme_id=${programmeId}`)).data,
  });
  if (species.length === 0) return null;

  return (
    <div className="py-8 border-b border-ptf-primary/12" data-testid="programme-species">
      <p className="text-[0.65rem] uppercase tracking-[0.24em] text-ptf-primary/50 mb-4 flex items-center gap-2"><PawPrint className="h-3.5 w-3.5" /> Species</p>
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
        {species.map((s) => (
          <button
            key={s.id}
            type="button"
            data-testid={`species-card-${s.id}`}
            onClick={() => setSelected(s)}
            className="text-left border border-ptf-primary/12 hover:border-ptf-primary/30 transition-colors overflow-hidden"
          >
            {s.images?.[0] ? (
              <div className="aspect-square overflow-hidden"><img src={resolveImg(s.images[0])} alt={s.common_name} className="h-full w-full object-cover" loading="lazy" /></div>
            ) : (
              <div className="aspect-square grid place-items-center bg-ptf-primary/5"><PawPrint className="h-6 w-6 text-ptf-primary/30" /></div>
            )}
            <div className="p-3">
              <p className="text-sm font-medium text-ptf-primary leading-tight">{s.common_name}</p>
              {s.scientific_name && <p className="text-[0.65rem] italic text-ptf-text/50 mt-0.5">{s.scientific_name}</p>}
              {s.conservation_status && <p className="mt-1.5 text-[0.58rem] uppercase tracking-[0.1em] text-ptf-secondary line-clamp-1">{s.conservation_status}</p>}
            </div>
          </button>
        ))}
      </div>
      <SpeciesDetailSheet species={selected} onClose={() => setSelected(null)} />
    </div>
  );
}

function RelatedContent({ p }) {
  const journeyIds = p.related_journey_ids || [];
  const filmSlugs = p.related_film_slugs || [];
  const storyIds = p.related_story_ids || [];
  const has = journeyIds.length + filmSlugs.length + storyIds.length > 0;

  const { data: journeys = [] } = useQuery({ queryKey: ["journeys"], queryFn: async () => (await api.get("/journeys")).data, enabled: journeyIds.length > 0 });
  const { data: films = [] } = useQuery({ queryKey: ["films"], queryFn: async () => (await api.get("/films")).data, enabled: filmSlugs.length > 0 });
  const { data: stories = [] } = useQuery({ queryKey: ["stories"], queryFn: async () => (await api.get("/stories")).data, enabled: storyIds.length > 0 });

  if (!has) return null;
  const relJourneys = journeys.filter((j) => journeyIds.includes(j.id));
  const relFilms = films.filter((f) => filmSlugs.includes(f.slug));
  const relStories = stories.filter((s) => storyIds.includes(s.id));
  const items = [
    ...relJourneys.map((j) => ({ to: `/expeditions/${j.id}`, label: "Expedition", title: j.title })),
    ...relFilms.map((f) => ({ to: `/films/${f.slug}`, label: "Film", title: f.title })),
    ...relStories.map((s) => ({ to: `/stories/${s.id}`, label: "Story", title: s.title })),
  ];
  if (items.length === 0) return null;

  return (
    <div className="pt-8" data-testid="programme-related-content">
      <p className="text-[0.65rem] uppercase tracking-[0.24em] text-ptf-primary/50 mb-4">Related Content</p>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        {items.map((it) => (
          <Link key={it.to} to={it.to} className="flex items-center justify-between gap-3 border border-ptf-primary/12 p-4 hover:border-ptf-primary/30 transition-colors">
            <div>
              <p className="text-[0.6rem] uppercase tracking-[0.14em] text-ptf-secondary">{it.label}</p>
              <p className="mt-1 text-ptf-primary font-medium">{it.title}</p>
            </div>
            <ArrowUpRight className="h-4 w-4 shrink-0 text-ptf-primary/50" />
          </Link>
        ))}
      </div>
    </div>
  );
}

export default function ProgrammeDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { data: p, isLoading, isError } = useQuery({
    queryKey: ["programme", id],
    queryFn: async () => (await api.get(`/programmes/${id}`)).data,
    retry: false,
  });

  useEffect(() => {
    if (!p) return;
    trackMetaEvent("ViewContent", { content_name: p.name, content_type: "programme", content_ids: [p.id] });
  }, [p]);

  if (isLoading) return <div className="min-h-screen grid place-items-center bg-ptf-bg"><Loader2 className="h-8 w-8 animate-spin text-ptf-primary" /></div>;
  if (isError || !p) {
    return (
      <div className="min-h-screen grid place-items-center bg-ptf-bg text-center px-6">
        <div>
          <h1 className="font-display uppercase text-4xl text-ptf-primary">Programme not found</h1>
          <div className="mt-6 flex justify-center"><PtfButton onClick={() => navigate("/programmes")}>Back to Programmes</PtfButton></div>
        </div>
      </div>
    );
  }

  const faqSchema = p.faq?.length > 0 ? {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: p.faq.map((f) => ({ "@type": "Question", name: f.q, acceptedAnswer: { "@type": "Answer", text: f.a } })),
  } : null;

  return (
    <>
      <Seo
        title={p.seo_title || p.name}
        path={`/programmes/${id}`}
        description={p.seo_description || p.short_description || p.objective || `A ${p.category} programme by Planet Trotter Foundation.`}
        image={p.images?.[0] ? resolveImg(p.images[0]) : undefined}
        jsonLd={faqSchema || {
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          itemListElement: [
            { "@type": "ListItem", position: 1, name: "Home", item: "https://planettrotterfoundation.com/" },
            { "@type": "ListItem", position: 2, name: "Programmes", item: "https://planettrotterfoundation.com/programmes" },
            { "@type": "ListItem", position: 3, name: p.name },
          ],
        }}
      />

      <DetailHero p={p} />

      <section className="bg-ptf-bg py-16 md:py-24">
        <div className="mx-auto max-w-[1200px] px-5 sm:px-8 lg:px-12 grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-8">
            <Block label="The Challenge">{p.challenge}</Block>
            <Block label="What We Are Trying To Do">{p.objective}</Block>
            <Block label="Who It Serves">{p.target_beneficiaries}</Block>

            <Accordion type="multiple" className="w-full">
              <DetailAccordionItem value="approach" title="Programme Approach">{p.approach}</DetailAccordionItem>
              <DetailAccordionItem value="technology" title="Technology / Methodology">{p.technology}</DetailAccordionItem>
              <DetailAccordionItem value="community" title="Community Participation">{p.community_role}</DetailAccordionItem>
            </Accordion>

            {p.activities?.length > 0 && (
              <div className="py-8 border-b border-ptf-primary/12">
                <p className="text-[0.65rem] uppercase tracking-[0.24em] text-ptf-primary/50 mb-4">Key Activities</p>
                <ul className="space-y-2.5">
                  {p.activities.map((a) => (
                    <li key={a} className="flex items-start gap-2.5 text-ptf-text/80 max-w-[720px]">
                      <Check className="h-4 w-4 mt-1 shrink-0 text-ptf-secondary" /> {a}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            <KpiGrid label="Targets" items={p.target_kpis} tone="target" />

            <SpeciesSection programmeId={p.id} />
            <TrainingLink p={p} />
            <VolunteeringBlock p={p} />
            <PartnershipBlock p={p} />

            <KpiGrid label="Actual Results (Verified)" items={p.actual_kpis} tone="verified" />
            <KpiGrid label="Verified Impact" items={p.verified_impact} tone="verified" />

            {(!p.actual_kpis?.length && !p.verified_impact?.length && p.target_kpis?.length > 0) && (
              <div data-testid="programme-no-verified-note" className="py-6 flex items-start gap-3 text-sm text-ptf-text/55">
                <ShieldCheck className="h-4 w-4 mt-0.5 shrink-0" />
                <p>No verified results have been recorded for this programme yet. Targets above represent proposed goals, not achieved outcomes.</p>
              </div>
            )}

            <Block label="Outcomes">{p.outcomes}</Block>
            <Block label="Impact">{p.impact}</Block>

            {(() => {
              const galleryItems = p.gallery?.length
                ? p.gallery
                : (p.images || []).slice(1).map((url) => ({ url, caption: "" }));
              if (galleryItems.length === 0) return null;
              return (
                <div className="pt-10 pb-8">
                  <p className="text-[0.65rem] uppercase tracking-[0.24em] text-ptf-primary/50 mb-4">From the field</p>
                  <ProgrammeGallery items={galleryItems} />
                </div>
              );
            })()}

            <RelatedContent p={p} />
          </div>

          <aside className="lg:col-span-4">
            <AtAGlance p={p} />
          </aside>
        </div>
      </section>
    </>
  );
}
